import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { PagedResults } from "@/shared/components/PagedResults";
import { organizationsErrorKind } from "./organizations-error";

type SharedProps = ComponentProps<typeof PagedResults>;

type OrganizationsPagedResultsProps = Omit<SharedProps, "errorMessage" | "canRetry" | "labels"> & {
  /** The failure of the read; mapped to our own copy, never shown raw. */
  readonly error: unknown;
  /** Copy of the loading skeleton; defaults to the organizations list wording. */
  readonly loadingLabel?: string;
};

/**
 * The shared paged-results view with the organizations copy: error kinds map
 * to `organizations.errors.*` and a 403 offers no retry (it cannot succeed and
 * every attempt spends the audited, throttled read budget).
 */
export function OrganizationsPagedResults({
  error,
  loadingLabel,
  ...rest
}: OrganizationsPagedResultsProps) {
  const { t } = useTranslation();
  const kind = organizationsErrorKind(error);
  return (
    <PagedResults
      {...rest}
      errorMessage={t(`organizations.errors.${kind}`)}
      canRetry={kind !== "forbidden"}
      labels={{
        loading: loadingLabel ?? t("organizations.loading"),
        retry: t("organizations.retry"),
        lastPage: t("organizations.lastPage"),
      }}
    />
  );
}
